import { createContext, useContext, useEffect, useState, ReactNode, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';

interface SocketContextType {
  socket: null;
  isConnected: boolean;
  onlineUsers: string[];
  emit: (event: string, data: unknown) => void;
  on: (event: string, callback: (data: unknown) => void) => void;
  off: (event: string, callback: (data: unknown) => void) => void;
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

interface SocketProviderProps {
  children: ReactNode;
}

const isCloudflareDeployment = () => {
  const apiUrl = process.env.REACT_APP_API_URL || '';
  return apiUrl.includes('workers.dev') || 
         window.location.hostname.includes('vantax.co.za') ||
         window.location.hostname.includes('pages.dev');
};

export function SocketProvider({ children }: SocketProviderProps) {
  const [isConnected, setIsConnected] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState<string[]>([]);
  const { user, token } = useAuth();
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const eventListenersRef = useRef<Map<string, Set<(data: unknown) => void>>>(new Map());

  const emit = useCallback((event: string, data: unknown) => {
    if (!isCloudflareDeployment()) {
      console.log('Socket emit (polling mode):', event, data);
    }
  }, []);

  const on = useCallback((event: string, callback: (data: unknown) => void) => {
    if (!eventListenersRef.current.has(event)) {
      eventListenersRef.current.set(event, new Set());
    }
    eventListenersRef.current.get(event)?.add(callback);
  }, []);

  const off = useCallback((event: string, callback: (data: unknown) => void) => {
    eventListenersRef.current.get(event)?.delete(callback);
  }, []);

  const fetchOnlineUsers = useCallback(async () => {
    if (!token) return;
    
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${apiUrl}/realtime/online-users`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setOnlineUsers(data.data.users || []);
        }
      }
    } catch (error) {
      // Silently fail - polling will retry
    }
  }, [token]);

  useEffect(() => {
    if (user && token) {
      if (isCloudflareDeployment()) {
        console.log('Using polling mode for Cloudflare deployment');
        setIsConnected(true);
        
        fetchOnlineUsers();
        
        pollingIntervalRef.current = setInterval(() => {
          fetchOnlineUsers();
        }, 30000);

        return () => {
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          setIsConnected(false);
        };
      } else {
        console.log('WebSocket mode disabled - using polling fallback');
        setIsConnected(true);
      }
    }
  }, [user, token, fetchOnlineUsers]);

  const value: SocketContextType = {
    socket: null,
    isConnected,
    onlineUsers,
    emit,
    on,
    off,
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
}

export function useSocket() {
  const context = useContext(SocketContext);
  if (context === undefined) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
}
